import React, { ReactNode } from 'react';
import { useRef, useState, MouseEvent, useMemo, useCallback } from 'react';
import { BaseChip } from './BaseChip';
import CustomSelect, { Option } from '../../CustomSelect';
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form';
import { Filter } from '../types';
import { useFormCustomContext } from '../index';

type Props<T extends string> = ControllerRenderProps<FieldValues, string> & {
  label: string;
  options: Filter<any>['options'];
  disabled?: boolean;
  isMulti?: boolean;
  tooltipTitle?: string | ReactNode;
  externalOnChange?: ({
    data,
    setValue,
  }: {
    data?: (Option | string | null)[];
    setValue?: UseFormSetValue<FieldValues>;
  }) => void;
};

export const SelectChip = <T extends string>({
  value,
  label,
  name,
  options,
  isMulti = false,
  disabled,
  tooltipTitle,
  externalOnChange,
}: Props<T>) => {
  const [selectionHappened, setSelectionHappened] = useState(false);
  const { setValue } = useFormContext();
  const { onChangeSubmit, formIsSubmittedOnFieldChange, setIsSubmitting } =
    useFormCustomContext();
  const anchorEl = useRef<HTMLElement | null>(null);
  const [isOpen, setOpen] = useState(false);

  const selectedOptions = useMemo(
    () =>
      options?.filter(({ value: optionValue }) =>
        value?.includes(optionValue),
      ) ?? [],
    [options, value],
  );

  const formattedSelectedValue = useMemo(() => {
    return selectedOptions.map((option) => option.label);
  }, [selectedOptions]);

  const styles = useMemo(
    () => ({
      menuPaper: {
        transform: `translateY(${
          anchorEl.current ? anchorEl.current.getBoundingClientRect().height : 0
        }px)`,
      },
    }),
    [],
  );

  const handleChange = useCallback(
    (selectedOption: Option[]) => {
      setSelectionHappened(true);

      if (isMulti) {
        if (!selectedOption.length) {
          setValue(name, selectedOption);
          externalOnChange?.({ data: selectedOption, setValue });
        }
        if (options && selectedOption.length === 1) {
          const selectedValue = selectedOption[0]?.value;

          if (value) {
            if (value?.includes(selectedValue)) {
              setValue(
                name,
                value.filter((item: string) => item !== selectedValue),
              );
              externalOnChange?.({
                data: value.filter((item: string) => item !== selectedValue),
                setValue,
              });
              return;
            }
            setValue(name, [...value, selectedValue]);
            externalOnChange?.({ data: [...value, selectedValue], setValue });
            return;
          }
          setValue(name, [selectedValue]);
          externalOnChange?.({ data: [selectedValue], setValue });
          return;
        }

        if (selectedOption.length === options?.length) {
          const values = selectedOption.map(({ value }) => value);
          setValue(name, values);
          externalOnChange?.({ data: values, setValue });
        }
      } else {
        const selectedValue = selectedOption[0]?.value;
        setValue(name, [selectedValue]);
        externalOnChange?.({ data: [selectedValue], setValue });
      }
    },
    [isMulti, name, externalOnChange, options, setValue, value],
  );

  const handleToggleChip = useCallback((e?: MouseEvent<HTMLElement>) => {
    setOpen((isOpen) => !isOpen);

    if (e) {
      anchorEl.current = e.currentTarget;
    }
  }, []);

  const handleOnClose = useCallback(
    async (e?: MouseEvent<HTMLElement>) => {
      handleToggleChip(e);
      if (formIsSubmittedOnFieldChange && selectionHappened) {
        setTimeout(onChangeSubmit, 0);
        setIsSubmitting(true);
      }
      setSelectionHappened(false);
    },
    [
      handleToggleChip,
      formIsSubmittedOnFieldChange,
      selectionHappened,
      onChangeSubmit,
      setIsSubmitting,
    ],
  );

  const handleResetValues = useCallback(async () => {
    setValue(name, null);
    externalOnChange?.({ setValue });

    if (formIsSubmittedOnFieldChange) {
      setIsSubmitting(true);
      await onChangeSubmit();
    }
  }, [
    formIsSubmittedOnFieldChange,
    setValue,
    onChangeSubmit,
    name,
    externalOnChange,
    setIsSubmitting,
  ]);

  return (
    <BaseChip
      name={name}
      formattedSelectedValue={formattedSelectedValue}
      label={label}
      isOpen={isOpen}
      disabled={disabled}
      tooltipTitle={tooltipTitle}
      resetValue={handleResetValues}
      onClick={handleToggleChip}
    >
      <CustomSelect
        type={isMulti ? 'AllOrOne' : 'OnlyOne'}
        open={isOpen}
        refElement={anchorEl}
        options={options ?? []}
        setSelectedOptions={handleChange}
        selectedOptions={selectedOptions}
        isSearchable
        onClose={handleOnClose}
        style={styles}
      />
    </BaseChip>
  );
};
